<script setup>
  import { computed, onMounted, nextTick, inject, ref } from "vue";
  import { storeToRefs } from "pinia";
  import { isCompactCard, isFoldedByDefault, isLogicCard } from "@utils/composables/reviewDocument";
  import { getCardGroupColors } from "@utils/constants/cardTypes";
  import { Button } from "@/components/ui/button";
  import { Tooltip } from "@/components/ui/tooltip";
  import { ChevronRight, Pencil } from "lucide-vue-next";
  import ReviewCardBody from "./ReviewCardBody.vue";
  import ReviewCardOptions from "./ReviewCardOptions.vue";
  import ReviewConditionLine from "./ReviewConditionLine.vue";
  import { useQuestionCardTypes, useCardAttributes } from "@utils/composables/questionCard";
  import { useReviewCard } from "@utils/composables/reviewCard";
  import { useCurrentCardStore } from "@stores/survey/currentCard/currentCard";
  import { useSurveyCardsStore } from "@stores/survey/surveyCards";
  import { useSurveyStore } from "@stores/survey/survey";
  import { useReviewCommentsStore } from "@stores/survey/reviewComments";
  import { isEmpty, renderToHtml } from "@utils/toolBox";

  const currentCardStore = useCurrentCardStore();
  const { currentToken } = currentCardStore;
  const { currentToken: selectedToken } = storeToRefs(currentCardStore);

  const { canWriteSurvey } = storeToRefs(useSurveyStore());

  const surveyCardsStore = useSurveyCardsStore();
  const { selectCurrentCard, redirectToEdit } = surveyCardsStore;
  const { firstLoadReviewPage } = storeToRefs(surveyCardsStore);

  const props = defineProps({
    card: { type: Object, required: true },
    cardSubTypesInfos: { type: Object, required: true },
    cardTypesGroups: { type: Array, required: true },
  });

  const compact = computed(() => isCompactCard(props.card));
  const logic = computed(() => isLogicCard(props.card));
  const foldable = computed(() => isFoldedByDefault(props.card));
  const open = ref(false);
  const showsBody = computed(() => !compact.value && (!foldable.value || open.value));

  const cardAppears = computed(() => !isEmpty(document.getElementById(`review-card-${currentToken}`)));

  onMounted(async () => {
    await nextTick(() => {
      if (cardAppears.value && firstLoadReviewPage.value)
        reviewEventBus.emit({ command: "scroll", token: currentToken });
    });
  });

  const { type, name, cardsTitle, hint, hasHint, token } = useCardAttributes();
  const { determineSubTypeLabel, determineIcon } = useQuestionCardTypes(props.cardSubTypesInfos);
  const { reviewCardFields, reviewCardOptions } = useReviewCard(props.card);
  const questionEventBus = inject("questionEventBus");
  const reviewEventBus = inject("reviewEventBus");
  const reviewStore = useReviewCommentsStore();
  const isQuestion = computed(() => props.card.type?.startsWith("q_"));
  const select = () => {
    selectCurrentCard({ token: props.card.token });
    reviewStore.openCardComments(props.card.token);
  };
  const editCard = (cardToken) => {
    selectCurrentCard({ token: cardToken });
    redirectToEdit({ redirect: true, token: cardToken });
    questionEventBus.emit({ command: "changeTab", token: cardToken });
  };
</script>

<template>
  <div
    :id="`review-card-${card.token}`"
    class="group relative rounded-xl border bg-card px-5 shadow-xs transition-shadow prose-a:text-primary prose-a:underline prose-a:underline-offset-2"
    :class="[
      logic || compact ? 'py-3' : 'py-5',
      card.is_for_analytics && 'opacity-60',
      selectedToken === card.token && 'border-episto-teal',
    ]"
    data-cy="review-card"
    @click="select">
    <div
      class="absolute top-3 right-3 flex items-center gap-0.5"
      @click.stop>
      <Tooltip
        v-if="canWriteSurvey"
        content="Edit">
        <Button
          variant="ghost"
          size="icon-sm"
          class="text-muted-foreground opacity-0 group-hover:opacity-100 hover:text-primary"
          @click="editCard(token(card))">
          <Pencil class="size-4" />
        </Button>
      </Tooltip>
    </div>
    <div class="flex flex-wrap items-center gap-2.5 pr-10">
      <button
        v-if="foldable"
        type="button"
        class="-ml-1 flex size-5 shrink-0 items-center justify-center rounded text-foreground-subtle hover:text-primary"
        :aria-expanded="open"
        :aria-label="open ? 'Hide details' : 'Show details'"
        @click="open = !open">
        <ChevronRight
          class="size-4 transition-transform"
          :class="open && 'rotate-90'" />
      </button>
      <Tooltip :content="determineSubTypeLabel(type(card))">
        <span
          class="relative flex size-5 shrink-0 cursor-help items-center justify-center"
          :class="getCardGroupColors(card).text">
          <component
            :is="determineIcon(type(card))"
            class="size-4"
            :stroke-width="1.8" />
          <button
            v-if="reviewStore.openCount(card.token)"
            type="button"
            class="absolute -top-0.5 -left-0.5 size-2 rounded-full bg-episto-teal-700 ring-2 ring-background"
            aria-label="Open comments"
            @click.stop="select" />
        </span>
      </Tooltip>
      <span class="text-13 font-semibold text-primary">{{ name(card) || "Untitled" }}</span>
      <ReviewConditionLine
        v-if="!isEmpty(card.specs?.conditions)"
        :conditions="card.specs.conditions"
        small />
      <ReviewCardOptions
        v-if="compact && reviewCardOptions.length"
        inline
        :card="card"
        :options="reviewCardOptions" />
    </div>
    <template v-if="showsBody">
      <!-- eslint-disable vue/no-v-html -->
      <p
        v-if="!isEmpty(cardsTitle(card))"
        class="mt-3 leading-snug text-foreground"
        :class="isQuestion ? 'text-base font-medium' : 'text-sm'"
        v-html="$sanitize(renderToHtml(cardsTitle(card)))"></p>
      <span
        v-if="hasHint(card)"
        class="mt-1 block text-13 italic text-muted-foreground"
        v-html="$sanitize(renderToHtml(hint(card)))"></span>
      <!--eslint-enable-->

      <div class="mt-3">
        <ReviewCardBody
          :card="card"
          :fields="reviewCardFields" />
      </div>

      <ReviewCardOptions
        v-if="!isEmpty(reviewCardOptions)"
        class="mt-4"
        :card="card"
        :options="reviewCardOptions" />
    </template>
  </div>
</template>
